import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-hot-toast'
import { useTransactionStore } from '@/store/useTransactionStore'

interface PaymentData {
  paymentUrl: string | null
  paymentSessionToken: string | null
  paymentSessionSecret: string | null
  paymentSession: any | null
  transactionId: string | null
  isLoading: boolean
}

const MAX_RETRIES = 3
const RETRY_DELAY = 500

/**
 * Hook for getting payment data from store or URL params
 * Handles 3DS redirects and regular payments
 */
export function usePaymentData(): PaymentData {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [retryCount, setRetryCount] = useState(0)
  const [isLoading, setIsLoading] = useState(true)
  const [paymentData, setPaymentData] = useState<PaymentData>({
    paymentUrl: null,
    paymentSessionToken: null,
    paymentSessionSecret: null,
    paymentSession: null,
    transactionId: null,
    isLoading: true,
  })

  const lastTransactionData = useTransactionStore(
    (state) => state.lastTransactionData,
  )

  // Check for 3DS redirect
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const is3DSRedirect = urlParams.has('cko-payment-id')

    if (is3DSRedirect) {
      setPaymentData({
        paymentUrl: null,
        paymentSessionToken: '3ds-redirect',
        paymentSessionSecret: '3ds-redirect',
        paymentSession: { order_id: '3ds-redirect' },
        transactionId: '3ds-redirect',
        isLoading: false,
      })
      setIsLoading(false)
    }
  }, [])

  // Load transaction data from store
  useEffect(() => {
    // If data already loaded (e.g. 3DS redirect), skip
    if (!isLoading) return

    // Get URL or session token from store
    if (!lastTransactionData) {
      if (retryCount < MAX_RETRIES) {
        const timer = setTimeout(() => {
          setRetryCount(retryCount + 1)
        }, RETRY_DELAY)
        return () => clearTimeout(timer)
      } else {
        console.error(
          '[usePaymentData] No transaction data found after retries',
        )
        toast.error(t('common:paymentDataNotFound'))
        navigate('/activity')
        return
      }
    }

    // Check for payment_url or payment_session_token
    if (
      !lastTransactionData.paymentUrl &&
      !lastTransactionData.paymentSessionToken
    ) {
      console.error('[usePaymentData] No payment URL or session token')
      toast.error(t('common:paymentUrlNotFound'))
      navigate('/activity')
      return
    }

    setPaymentData({
      paymentUrl: lastTransactionData.paymentUrl || null,
      paymentSessionToken: lastTransactionData.paymentSessionToken || null,
      paymentSessionSecret: lastTransactionData.paymentSessionSecret || null,
      paymentSession: lastTransactionData || null,
      transactionId: lastTransactionData.transactionId,
      isLoading: false,
    })
    setIsLoading(false)
  }, [lastTransactionData, navigate, retryCount, t, isLoading])

  return { ...paymentData, isLoading }
}
